import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { BillingApiError } from "../client"
import type { SubscriptionResponse } from "../types"
import { shouldRetryBillingQuery } from "../utils/billingQueryRetry"
import { useBillingClientContext } from "./useBillingClient"

interface UseBillingSubscriptionOptions {
  enabled?: boolean
  refetchInterval?: number | false
}

const BILLING_SUBSCRIPTION_QUERY_KEY = ["billing", "subscription"] as const

export function getBillingSubscriptionQueryKey(
  organizationSlug: string | null,
) {
  return [...BILLING_SUBSCRIPTION_QUERY_KEY, organizationSlug] as const
}

export function useBillingSubscription(
  options: UseBillingSubscriptionOptions = {},
): UseQueryResult<SubscriptionResponse, BillingApiError> {
  const { client, isReady, organizationSlug, routeOrganizationMissing } =
    useBillingClientContext()

  return useQuery<SubscriptionResponse, BillingApiError>({
    queryKey: getBillingSubscriptionQueryKey(organizationSlug),
    queryFn: () => {
      if (routeOrganizationMissing) {
        throw new BillingApiError(
          404,
          {
            error_code: "ORGANIZATION_NOT_FOUND",
            message: "Route organization not found in memberships.",
          },
          "Failed to load subscription",
        )
      }
      return client.getSubscription()
    },
    enabled: (options.enabled ?? true) && isReady,
    refetchInterval: options.refetchInterval,
    retry: shouldRetryBillingQuery,
  })
}
